import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useCurrentUserContext } from '@/contexts';
import { OrganizationRole } from '@/types/gql.generated';
import {
  Avatar,
  Box,
  Button,
  Flex,
  type FlexProps,
  Text,
  useDisclosure,
} from '@/ui';
import { PlanBadge } from '../../../components/PlanBadge';
import type { OrgMember } from '../hooks/useOrganizationMembers';
import { MemberPhone } from './MemberPhone';

type Props = FlexProps & {
  member: OrgMember;
  organizationName: string;
  isRemoving?: boolean;
  onRemove: (userId: string, options?: { onSuccess?: () => void }) => void;
  /** FAMILY orgs: show SMS contact details */
  smsContactsEnabled?: boolean;
  onEditContact?: (member: OrgMember) => void;
};

export const MemberRow = ({
  member,
  organizationName,
  isRemoving,
  onRemove,
  smsContactsEnabled,
  onEditContact,
  ...flexProps
}: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations',
  });
  const { currentUser } = useCurrentUserContext();
  const {
    isOpen: isRemoveConfirmOpen,
    onOpen: openRemoveConfirm,
    onClose: closeRemoveConfirm,
  } = useDisclosure();

  const { user, role } = member;
  const isOwner = role === OrganizationRole.Owner;
  const isMe = currentUser?.id === user.id;
  const name = user.name || user.email || '';
  // Owners can't be removed, and members can't remove themselves here.
  const canRemove = !isOwner && !isMe;

  return (
    <>
      <ConfirmDialog
        confirmButtonText={t('member.remove')}
        isLoading={isRemoving}
        isOpen={isRemoveConfirmOpen}
        title={t('member.remove_confirm_title', {
          name,
          organization: organizationName,
        })}
        onCancel={closeRemoveConfirm}
        onConfirm={() => onRemove(user.id, { onSuccess: closeRemoveConfirm })}
      />

      <Flex align="center" {...flexProps}>
        <Avatar mr="4" name={name} size="sm" src={user.avatar ?? undefined} />
        <Box flex="1" lineHeight="normal">
          <Text fontWeight="medium">
            {member.memberName || user.name || user.email}{' '}
            {isMe && t('member.you')}
          </Text>
          {(member.memberName || user.name) && user.email && (
            <Text color="customgray.mid" fontSize="sm">
              {user.email}
            </Text>
          )}
          {smsContactsEnabled && (
            <MemberPhone
              phoneNumber={member.phoneNumber}
              onAddPhone={
                onEditContact ? () => onEditContact(member) : undefined
              }
            />
          )}
        </Box>
        <PlanBadge mr="3" tone={isOwner ? 'role' : 'plan'}>
          {isOwner ? t('member.role_owner') : t('member.role_editor')}
        </PlanBadge>
        {smsContactsEnabled && onEditContact && (
          <Button
            colorScheme="dark"
            fontSize="md"
            size="sm"
            variant="ghost"
            onClick={() => onEditContact(member)}
          >
            {t('member.edit_contact')}
          </Button>
        )}
        {canRemove && (
          <Button
            colorScheme="dark"
            fontSize="md"
            mr="-4"
            size="sm"
            variant="ghost"
            onClick={openRemoveConfirm}
          >
            {t('member.remove')}
          </Button>
        )}
      </Flex>
    </>
  );
};
